// IST (UTC+5:30) helpers. The date users see should reflect Asia/Kolkata —
// so `istToday()` flips the calendar date the moment IST crosses midnight,
// regardless of the browser's local timezone.

const IST_OFFSET_MINUTES = 5 * 60 + 30;

/** Return the current IST Date (still a JS Date, but shifted so UTC fields equal IST). */
function _nowAsIST() {
  const now = new Date();
  // shift: local → UTC → + IST offset
  const utcMs = now.getTime();
  return new Date(utcMs + IST_OFFSET_MINUTES * 60_000);
}

/** YYYY-MM-DD in IST. Safe to feed <input type="date" /> or API date filters. */
export function istToday() {
  const d = _nowAsIST();
  return d.toISOString().slice(0, 10);
}

/** Given an ISO string (UTC), render it in IST as a human-friendly local-time string.
 * Format: DD/MM/YYYY, HH:MM AM/PM
 */
export function formatIST(iso, opts = { time: true }) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    // Convert to IST
    const istDate = new Date(d.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
    
    const day = String(istDate.getDate()).padStart(2, "0");
    const month = String(istDate.getMonth() + 1).padStart(2, "0");
    const year = istDate.getFullYear();
    
    if (!opts.time) {
      return `${day}/${month}/${year}`;
    }
    
    let hours = istDate.getHours();
    const minutes = String(istDate.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    
    return `${day}/${month}/${year}, ${hours}:${minutes} ${ampm}`;
  } catch {
    return iso;
  }
}

/** For `<input type="date" max>`. Also IST today — blocks picking tomorrow until IST flips. */
export function istMaxSelectableDate() {
  return istToday();
}

/**
 * Calculate calendar-day difference between a timestamp and now, using IST calendar dates.
 * Returns the number of calendar days difference (positive = past, negative = future).
 * 
 * This uses CALENDAR DAYS, not 24-hour periods:
 * - Same IST calendar date → 0
 * - Previous IST calendar date → 1
 * - Two calendar dates ago → 2
 * 
 * Example:
 * - Sale: 25/08/2026 7:25 PM IST
 * - Viewed: 27/08/2026 10:00 AM IST
 * - Result: 2 (even though only ~38 hours elapsed)
 */
export function getCalendarDaysDiff(dateStr) {
  if (!dateStr) return null;
  
  try {
    const date = new Date(dateStr);
    const now = new Date();
    
    // Convert both to IST calendar dates (YYYY-MM-DD format)
    // Using toLocaleDateString with IST timezone ensures correct calendar date
    const dateIST = date.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }); // YYYY-MM-DD
    const nowIST = now.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });   // YYYY-MM-DD
    
    // Parse back to Date objects at midnight for comparison
    const dateMidnight = new Date(dateIST + "T00:00:00");
    const nowMidnight = new Date(nowIST + "T00:00:00");
    
    // Calculate difference in days
    const diffMs = nowMidnight - dateMidnight;
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
    
    return diffDays;
  } catch {
    return null;
  }
}

/**
 * Format a timestamp as a relative time label based on IST calendar days.
 * 
 * Labels:
 * - Same IST calendar date → "Today"
 * - Previous IST calendar date → "Yesterday"
 * - 2-6 calendar dates ago → "X days ago"
 * - 7-29 calendar dates ago → "X weeks ago"
 * - 30-364 calendar dates ago → "X months ago"
 * - 365+ calendar dates ago → Full date
 * - Future dates → "In X days" or full date
 * 
 * @param {string} dateStr - ISO timestamp string
 * @returns {string} Relative time label
 */
export function formatRelativeTimeIST(dateStr) {
  if (!dateStr) return "—";
  
  const diffDays = getCalendarDaysDiff(dateStr);
  if (diffDays === null) return "—";
  
  // Past dates
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays > 1 && diffDays < 7) return `${diffDays} days ago`;
  if (diffDays >= 7 && diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
  if (diffDays >= 30 && diffDays < 365) return `${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) > 1 ? 's' : ''} ago`;
  
  // Future dates
  if (diffDays < 0) {
    const futureDays = Math.abs(diffDays);
    if (futureDays === 1) return "Tomorrow";
    if (futureDays < 7) return `In ${futureDays} days`;
    return new Date(dateStr).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" });
  }
  
  // Very old dates - show full date
  return new Date(dateStr).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" });
}
